/**
 * AI CRM React Query Hooks
 * Custom hooks for data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiCrmApi } from '../api/aiCrmApi';
import { LeadFilters, CreateLeadInput, UpdateLeadInput } from '../types';

// Query Keys
export const AI_CRM_KEYS = {
  all: ['ai-crm'] as const,
  dashboard: () => [...AI_CRM_KEYS.all, 'dashboard'] as const,
  dashboardSummary: () => [...AI_CRM_KEYS.dashboard(), 'summary'] as const,
  leads: () => [...AI_CRM_KEYS.all, 'leads'] as const,
  leadsList: (filters?: LeadFilters) => [...AI_CRM_KEYS.leads(), 'list', filters] as const,
  leadDetail: (id: string) => [...AI_CRM_KEYS.leads(), 'detail', id] as const,
  leadActivities: (id: string) => [...AI_CRM_KEYS.leads(), id, 'activities'] as const,
};

/**
 * Hook to fetch dashboard summary
 */
export const useDashboardSummary = () => {
  return useQuery({
    queryKey: AI_CRM_KEYS.dashboardSummary(),
    queryFn: () => aiCrmApi.dashboard.getSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
  });
};

/**
 * Hook to fetch leads with filters
 */
export const useLeads = (filters?: LeadFilters & { limit?: number; offset?: number }) => {
  return useQuery({
    queryKey: AI_CRM_KEYS.leadsList(filters),
    queryFn: () => aiCrmApi.leads.getLeads(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch single lead
 */
export const useLead = (id: string) => {
  return useQuery({
    queryKey: AI_CRM_KEYS.leadDetail(id),
    queryFn: () => aiCrmApi.leads.getLeadById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create a new lead
 */
export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLeadInput) => aiCrmApi.leads.createLead(data),
    onSuccess: () => {
      // Invalidate leads list to refetch
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.leads() });
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.dashboardSummary() });
    },
  });
};

/**
 * Hook to update a lead
 */
export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeadInput }) =>
      aiCrmApi.leads.updateLead(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific lead and list
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.leadDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.leads() });
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.dashboardSummary() });
    },
  });
};

/**
 * Hook to delete a lead
 */
export const useDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiCrmApi.leads.deleteLead(id),
    onSuccess: () => {
      // Invalidate leads list
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.leads() });
      queryClient.invalidateQueries({ queryKey: AI_CRM_KEYS.dashboardSummary() });
    },
  });
};

/**
 * Hook to fetch lead activities
 */
export const useLeadActivities = (leadId: string | null) => {
  return useQuery({
    queryKey: AI_CRM_KEYS.leadActivities(leadId || ''),
    queryFn: () => aiCrmApi.leads.getLeadActivities(leadId || ''),
    enabled: !!leadId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};
